import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarCheck,
  ChartColumnBig,
  ChevronDown,
  ClipboardList,
  Crown,
  Pill,
  Coins,
  DollarSign,
  FileCheck2,
  FlaskConical,
  Handshake,
  Headset,
  HeartHandshake,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MessagesSquare,
  Newspaper,
  Package,
  PawPrint,
  PieChart,
  ScrollText,
  Shield,
  Sparkles,
  Stethoscope,
  Tablets,
  Ticket,
  User,
  UserCog,
  Users,
  type LucideIcon,
  Siren,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAlertasAdminControllerResumo } from '@/api/generated/admin-alertas/admin-alertas';
import { ParceirosCarousel } from '@/app/layout/ParceirosCarousel';
import { cn } from '@/shared/lib/cn';
import { ROTAS } from '@/shared/lib/rotas';
import type { PermissaoChave } from '@/shared/permissoes/chaves';
import { hasPermission } from '@/shared/permissoes/hasPermission';

export interface ItemNav {
  nome: string;
  para: string;
  icone: LucideIcon;
  /** Ausente = fora do catálogo de permissões (PLANO-MVP §4), sempre visível. */
  permissao?: PermissaoChave;
  /**
   * Rota que existe mas não aparece no menu.
   *
   * ⚠️ Continua no `ITENS_NAV` de propósito: é daqui que o `AppLayout` tira o
   * TÍTULO do header (`achatarNav`). Tirar a entrada esconderia o item e faria a
   * tela abrir com o título "Sistema" — esconder da nav e perder o nome da
   * página são coisas diferentes.
   */
  oculto?: boolean;
  /** Contador ao lado do nome. Hoje só os alertas críticos em aberto. */
  selo?: 'alertasCriticos';
}

export interface GrupoNav {
  nome: string;
  icone: LucideIcon;
  filhos: readonly ItemNav[];
}

export type EntradaNav = ItemNav | GrupoNav;

function ehGrupo(entrada: EntradaNav): entrada is GrupoNav {
  return 'filhos' in entrada;
}

/**
 * Navegação da aplicação.
 *
 * ⚠️ O grupo "Administração" é filtrado por **permissão**, nunca por papel. A
 * V1 usava `isAdmin`, e era exatamente por isso que ela mostrava tela que o
 * cargo não podia abrir — o item aparecia, o clique dava erro.
 */
export const ITENS_NAV: EntradaNav[] = [
  { nome: 'Dashboard', para: ROTAS.dashboard, icone: LayoutDashboard },
  // ⚠️ **Tutores saiu do menu, e a ROTA continua de pé.** O cadastro de tutor
  // agora vive dentro do formulário do paciente (o `SeletorDeTutor`, com busca e
  // com o `+`), que é onde a informação de fato falta — e uma tela dedicada a
  // gerenciar tutores, na prática, só era aberta por engano no caminho de
  // cadastrar o animal. `oculto` em vez de remover: `/tutores` ainda responde
  // (link antigo, favorito do navegador) e o header ainda sabe o nome da tela.
  { nome: 'Responsáveis', para: ROTAS.tutores, icone: Users, permissao: 'tutores.visualizar', oculto: true },
  {
    nome: 'Pacientes',
    para: ROTAS.pacientes,
    icone: PawPrint,
    permissao: 'pacientes.visualizar',
  },
  {
    nome: 'Exames',
    para: ROTAS.exames,
    icone: FlaskConical,
    permissao: 'exames.visualizar',
  },
  // Depois de "Exames": a análise comparativa é a leitura POPULACIONAL do que
  // aquela tela mostra caso a caso.
  {
    nome: 'Análise comparativa',
    para: ROTAS.analises,
    icone: ChartColumnBig,
    permissao: 'analises.visualizar',
  },
  {
    nome: 'Requisições',
    para: ROTAS.requisicoes,
    icone: ClipboardList,
    permissao: 'requisicoes_exames.visualizar',
  },
  {
    nome: 'Receituários',
    para: ROTAS.receituarios,
    icone: Pill,
    permissao: 'receituarios.visualizar',
  },
  {
    nome: 'Parceiros',
    para: ROTAS.parceiros,
    icone: Handshake,
    permissao: 'parceiros.visualizar',
  },
  // Sem `permissao`, como Dashboard e Perfil (PLANO-MVP §4): o Clube é sempre
  // acessível, e o que muda por ambiente é o RANQUE, não o direito de entrar.
  //
  // ⚠️ `Crown`, e não `Shield`: o escudo já é o ícone do grupo Administração
  // logo abaixo, e dois significados para o mesmo glifo na mesma sidebar é
  // exatamente o tipo de coisa que ninguém reporta e todo mundo estranha.
  { nome: 'Clube Hormonalle', para: ROTAS.clube, icone: Crown },
  // `discussoes.criar`, não `.visualizar`: quem abre uma discussão nova
  // (chama um especialista) é o médico. O advisor também tem
  // `discussoes.visualizar` — para acompanhar o que já foi aberto para ele —
  // mas não deve ver este item, que é "escolher e chamar alguém".
  {
    nome: 'Conexão com especialista',
    para: ROTAS.discussoes,
    icone: MessagesSquare,
    permissao: 'discussoes.criar',
  },
  // Só quem TEM `assessoria.*` (o cargo assessor) vê este — é o trabalho de
  // atender, separado de "Conexão com especialista" acima, que é abrir
  // discussão com alguém (só quem tem `discussoes.criar`, isto é, o médico).
  {
    nome: 'Atendimentos',
    para: ROTAS.atendimentos,
    icone: HeartHandshake,
    permissao: 'assessoria.visualizar',
  },
  // Artigos do advisor — aparecem para o médico no rodapé da discussão.
  {
    nome: 'Meus artigos',
    para: ROTAS.meusArtigos,
    icone: BookOpen,
    permissao: 'assessoria.atender',
  },
  // `suporte.visualizar`, não `.criar`: quem só acompanha os chamados do
  // ambiente (sem poder abrir) ainda precisa do item no menu. O botão "Abrir
  // chamado" dentro da tela é que checa `suporte.criar`.
  { nome: 'Suporte', para: ROTAS.suporte, icone: LifeBuoy, permissao: 'suporte.visualizar' },
  { nome: 'Perfil', para: ROTAS.perfil, icone: User },
  // Administração do PRÓPRIO ambiente pelo médico dono. Sai de graça do filtro
  // por permissão: só o cargo `medico` (e `assessor`) tem `ambiente.*`.
  {
    nome: 'Minha equipe',
    para: ROTAS.equipe,
    icone: UserCog,
    permissao: 'ambiente.visualizar',
  },
  // Portal do parceiro. A V1 trocava a nav inteira com um `if (isParceiro)`
  // (`Layout.tsx:169`); aqui sai de graça do filtro por permissão, porque o
  // cargo `parceiro` não tem nenhuma chave clínica.
  {
    nome: 'Meus materiais',
    para: ROTAS.portalMateriais,
    icone: Newspaper,
    permissao: 'materiais.visualizar',
  },
  {
    nome: 'Administração',
    icone: Shield,
    filhos: [
      {
        nome: 'Usuários',
        para: ROTAS.adminUsuarios,
        icone: Users,
        permissao: 'admin_usuarios.visualizar',
      },
      {
        nome: 'Cargos e permissões',
        para: ROTAS.adminCargos,
        icone: KeyRound,
        permissao: 'admin_cargos.visualizar',
      },
      {
        nome: 'Ambientes',
        para: ROTAS.adminAmbientes,
        icone: Building2,
        permissao: 'admin_tenants.visualizar',
      },
      {
        nome: 'Portfólio de exames',
        para: ROTAS.adminCatalogoExames,
        icone: FlaskConical,
        permissao: 'admin_catalogo_exames.visualizar',
      },
      {
        nome: 'Catálogo de fármacos',
        para: ROTAS.adminCatalogoFarmacos,
        icone: Tablets,
        permissao: 'admin_catalogo_farmacos.visualizar',
      },
      {
        nome: 'Catálogo de diagnósticos',
        para: ROTAS.adminCatalogoDiagnosticos,
        icone: Stethoscope,
        permissao: 'admin_catalogo_diagnosticos.visualizar',
      },
      {
        nome: 'Parceiros',
        para: ROTAS.adminParceiros,
        icone: Handshake,
        permissao: 'admin_parceiros.visualizar',
      },
      {
        nome: 'Produtos',
        para: ROTAS.adminProdutosParceiros,
        icone: Package,
        permissao: 'admin_produtos_parceiros.visualizar',
      },
      {
        nome: 'Aprovar materiais',
        para: ROTAS.adminMateriais,
        icone: FileCheck2,
        permissao: 'admin_materiais.visualizar',
      },
      {
        nome: 'Métricas',
        para: ROTAS.adminMetricasParceiros,
        icone: BarChart3,
        permissao: 'admin_metricas.visualizar',
      },
      {
        nome: 'Cupons',
        para: ROTAS.adminCupons,
        icone: Ticket,
        permissao: 'admin_cupons.visualizar',
      },
      {
        nome: 'Unidades',
        para: ROTAS.adminUnidades,
        icone: Coins,
        permissao: 'admin_unidades.visualizar',
      },
      {
        nome: 'Auditoria',
        para: ROTAS.adminAuditoria,
        icone: ScrollText,
        permissao: 'admin_auditoria.visualizar',
      },
      {
        nome: 'Relatórios',
        para: ROTAS.adminRelatorios,
        icone: PieChart,
        permissao: 'admin_relatorios.visualizar',
      },
      {
        nome: 'Preço da discussão',
        para: ROTAS.adminPrecosDiscussao,
        icone: DollarSign,
        permissao: 'admin_precos_discussao.visualizar',
      },
      {
        nome: 'Especialidades de discussão',
        para: ROTAS.adminEspecialidadesDiscussao,
        icone: Sparkles,
        permissao: 'admin_especialidades_discussao.visualizar',
      },
      {
        nome: 'Advisors',
        para: ROTAS.adminAdvisors,
        icone: CalendarCheck,
        permissao: 'admin_advisors.visualizar',
      },
      // `Headset` e não `LifeBuoy`: a boia já é o item "Suporte" de cima, que é
      // PEDIR ajuda. Este é atendê-la — mesmo cuidado que separou `Crown` de
      // `Shield` no Clube.
      {
        nome: 'Suporte',
        para: ROTAS.adminSuporte,
        icone: Headset,
        permissao: 'admin_suporte.visualizar',
      },
      {
        nome: 'Alertas',
        para: ROTAS.adminAlertas,
        icone: Siren,
        permissao: 'admin_alertas.visualizar',
        selo: 'alertasCriticos',
      },
    ],
  },
];

/**
 * Aplica o filtro de permissão (e o `oculto`), recursivamente.
 *
 * ⚠️ Grupo cujos filhos foram TODOS filtrados é descartado inteiro — senão
 * sobra um cabeçalho "Administração" que abre no vazio.
 */
export function filtrarNav(
  entradas: readonly EntradaNav[],
  permissoes: readonly string[] | undefined,
): EntradaNav[] {
  const visiveis: EntradaNav[] = [];

  const exibivel = (item: ItemNav) =>
    !item.oculto && (item.permissao == null || hasPermission(permissoes, item.permissao));

  for (const entrada of entradas) {
    if (!ehGrupo(entrada)) {
      if (exibivel(entrada)) visiveis.push(entrada);
      continue;
    }

    const filhos = entrada.filhos.filter(exibivel);
    if (filhos.length > 0) visiveis.push({ ...entrada, filhos });
  }

  return visiveis;
}

/** Achata a árvore. O `AppLayout` usa para derivar o título da página. */
export function achatarNav(entradas: readonly EntradaNav[]): ItemNav[] {
  return entradas.flatMap((entrada) => (ehGrupo(entrada) ? [...entrada.filhos] : [entrada]));
}

const CLASSE_ITEM =
  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150';

interface Props {
  aberta: boolean;
  aoFechar: () => void;
  aoSair: () => void;
  permissoes: readonly string[] | undefined;
  /** Injetável para teste. Em produção é sempre `ITENS_NAV`. */
  itensNav?: readonly EntradaNav[];
}

export function Sidebar({ aberta, aoFechar, aoSair, permissoes, itensNav = ITENS_NAV }: Props) {
  const entradas = filtrarNav(itensNav, permissoes);

  return (
    <>
      {aberta && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={aoFechar}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-sidebar border-r border-sidebar-border',
          'transform transition-transform duration-300 ease-in-out lg:translate-x-0',
          aberta ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-brand/10 bg-gradient-to-br from-nohra-turquoise/10 via-nohra-purple/10 to-nohra-magenta/10 flex justify-center">
            <img
              src="/logo-nohra-light.png"
              alt="NOHRA Clinic"
              className="w-36 h-auto object-contain dark:hidden"
            />
            <img
              src="/logo-nohra-dark.png"
              alt="NOHRA Clinic"
              className="w-36 h-auto object-contain hidden dark:block"
            />
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {entradas.map((entrada) =>
              ehGrupo(entrada) ? (
                <GrupoDeNav key={entrada.nome} grupo={entrada} aoNavegar={aoFechar} />
              ) : (
                <LinkDeNav key={entrada.para} item={entrada} aoNavegar={aoFechar} />
              ),
            )}
          </nav>

          <div className="border-t border-brand/10 p-3 space-y-3">
            <ParceirosCarousel />

            <button
              onClick={aoSair}
              className={cn(CLASSE_ITEM, 'w-full text-sidebar-foreground hover:bg-muted')}
            >
              <LogOut className="w-5 h-5 text-muted-foreground" />
              Sair
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function LinkDeNav({
  item,
  aoNavegar,
  recuado,
}: {
  item: ItemNav;
  aoNavegar: () => void;
  recuado?: boolean;
}) {
  return (
    <NavLink
      to={item.para}
      onClick={aoNavegar}
      className={({ isActive }) =>
        cn(
          CLASSE_ITEM,
          recuado && 'pl-6',
          isActive ? 'bg-brand text-brand-foreground' : 'text-sidebar-foreground hover:bg-muted',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icone
            className={cn('w-5 h-5', isActive ? 'text-brand-foreground' : 'text-muted-foreground')}
          />
          {item.nome}
          {item.selo === 'alertasCriticos' && <SeloDeAlertasCriticos ativo={isActive} />}
        </>
      )}
    </NavLink>
  );
}

/** Intervalo do contador do menu — o resumo é uma query agrupada, barata. */
const INTERVALO_DO_SELO_MS = 60_000;

/**
 * Quantos alertas CRÍTICOS estão em aberto. Só monta para quem vê o item —
 * o filtro de permissão do menu já tirou quem não tem `admin_alertas`.
 */
function SeloDeAlertasCriticos({ ativo }: { ativo: boolean }) {
  const { data } = useAlertasAdminControllerResumo({ query: { refetchInterval: INTERVALO_DO_SELO_MS } });
  const total = data?.criticosAbertos ?? 0;
  if (total === 0) return null;

  return (
    <span
      className={cn(
        'ml-auto min-w-5 rounded-full px-1.5 text-center text-xs font-semibold tabular-nums',
        ativo ? 'bg-brand-foreground text-brand' : 'bg-destructive text-destructive-foreground',
      )}
      aria-label={`${total} ${total === 1 ? 'alerta crítico' : 'alertas críticos'} em aberto`}
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}

function GrupoDeNav({ grupo, aoNavegar }: { grupo: GrupoNav; aoNavegar: () => void }) {
  const { pathname } = useLocation();
  const temFilhoAtivo = grupo.filhos.some((filho) => pathname.startsWith(filho.para));

  // Nasce ABERTO. O grupo só existe para quem tem alguma permissão dele, então
  // não é poluição para o resto — e obrigar um clique a cada visita seria
  // atrito puro para quem usa a área todo dia. O colapso fica para quem quiser
  // arrumar a sidebar.
  const [aberto, setAberto] = useState(true);

  // Navegar direto para uma URL de admin reabre o grupo, caso o usuário o tenha
  // fechado — senão o item ativo fica escondido dentro de um grupo colapsado.
  useEffect(() => {
    if (temFilhoAtivo) setAberto(true);
  }, [temFilhoAtivo]);

  return (
    <div className="pt-4 mt-4 border-t border-brand/10 space-y-1">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-brand uppercase tracking-wider hover:bg-muted rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2">
          <grupo.icone className="w-4 h-4" />
          {grupo.nome}
        </span>
        <ChevronDown
          className={cn('w-4 h-4 transition-transform duration-150', !aberto && '-rotate-90')}
        />
      </button>

      {aberto &&
        grupo.filhos.map((filho) => (
          <LinkDeNav key={filho.para} item={filho} aoNavegar={aoNavegar} recuado />
        ))}
    </div>
  );
}
